import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { submitWaitlist } from "../lib/api";

const initial = { full_name: "", whatsapp: "", email: "", city: "Trivandrum" };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mirrors backend/deps.py's WaitlistIn validation so a user sees the same
// rule client-side, before ever hitting the network.
function validateField(name, value) {
  switch (name) {
    case "full_name":
      return value.trim() ? "" : "Enter your full name.";
    case "whatsapp": {
      if (!value.trim()) return "Enter your WhatsApp number.";
      const digits = value.replace(/\D/g, "");
      return digits.length < 6 ? "That number looks too short." : "";
    }
    case "email": {
      const v = value.trim();
      if (!v) return "Enter your email address.";
      return EMAIL_RE.test(v) ? "" : "Enter a valid email address.";
    }
    default:
      return "";
  }
}

function validateAll(form) {
  return {
    full_name: validateField("full_name", form.full_name),
    whatsapp: validateField("whatsapp", form.whatsapp),
    email: validateField("email", form.email),
  };
}

export const WaitlistForm = ({ variant = "cream" }) => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const isDark = variant === "dark";
  const labelColor = isDark ? "text-white/60" : "text-ink/60";

  const fieldError = (name) => (touched[name] ? errors[name] || "" : "");
  const fieldClass = (name) =>
    `ed-input ${isDark ? "ed-input-dark" : ""} ${fieldError(name) ? "ed-input-error" : ""}`.trim();

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    // Live-clear an error as soon as the field becomes valid; don't wait for blur.
    if (touched[name]) {
      setErrors((er) => ({ ...er, [name]: validateField(name, value) }));
    }
  };

  const onBlur = (e) => {
    const { name, value } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
    setErrors((er) => ({ ...er, [name]: validateField(name, value) }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const nextErrors = validateAll(form);
    setErrors(nextErrors);
    setTouched({ full_name: true, whatsapp: true, email: true });
    if (Object.values(nextErrors).some(Boolean)) return;

    setSubmitting(true);
    try {
      await submitWaitlist(form);
      toast.success("You're on the list.");
      navigate("/success");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        // FastAPI validation error shape — map each one back onto its field
        // instead of a generic toast, so it shows up right where it's relevant.
        const fieldErrors = {};
        detail.forEach((d) => {
          const field = d.loc?.[d.loc.length - 1];
          if (field) fieldErrors[field] = d.msg;
        });
        setErrors((er) => ({ ...er, ...fieldErrors }));
        setTouched((t) => ({
          ...t,
          ...Object.fromEntries(Object.keys(fieldErrors).map((k) => [k, true])),
        }));
      } else {
        toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="w-full" data-testid="waitlist-form" noValidate>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
        <div className="flex flex-col">
          <label className={`text-[0.68rem] uppercase tracking-[0.22em] font-bold ${labelColor} pt-2`}>
            Full name
          </label>
          <input
            data-testid="waitlist-input-name"
            name="full_name"
            type="text"
            value={form.full_name}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="Aisha Menon"
            className={fieldClass("full_name")}
            aria-invalid={!!fieldError("full_name")}
          />
          <p className="text-coral text-xs mt-1 min-h-[1.2em]" data-testid="waitlist-error-name">
            {fieldError("full_name")}
          </p>
        </div>

        <div className="flex flex-col">
          <label className={`text-[0.68rem] uppercase tracking-[0.22em] font-bold ${labelColor} pt-2`}>
            WhatsApp number
          </label>
          <input
            data-testid="waitlist-input-whatsapp"
            name="whatsapp"
            type="tel"
            value={form.whatsapp}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="+91 98470 12345"
            className={fieldClass("whatsapp")}
            aria-invalid={!!fieldError("whatsapp")}
          />
          <p className="text-coral text-xs mt-1 min-h-[1.2em]" data-testid="waitlist-error-whatsapp">
            {fieldError("whatsapp")}
          </p>
        </div>

        <div className="flex flex-col">
          <label className={`text-[0.68rem] uppercase tracking-[0.22em] font-bold ${labelColor} pt-2`}>
            Email address
          </label>
          <input
            data-testid="waitlist-input-email"
            name="email"
            type="email"
            value={form.email}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="you@yourdomain.com"
            className={fieldClass("email")}
            aria-invalid={!!fieldError("email")}
          />
          <p className="text-coral text-xs mt-1 min-h-[1.2em]" data-testid="waitlist-error-email">
            {fieldError("email")}
          </p>
        </div>

        <div className="flex flex-col">
          <label className={`text-[0.68rem] uppercase tracking-[0.22em] font-bold ${labelColor} pt-2`}>
            City
          </label>
          <input
            data-testid="waitlist-input-city"
            name="city"
            type="text"
            required
            value="Trivandrum"
            readOnly
            aria-readonly="true"
            tabIndex={-1}
            className={`${fieldClass("city")} cursor-not-allowed opacity-90`}
          />
          <p className="text-xs mt-1 min-h-[1.2em]" aria-hidden="true" />
        </div>
      </div>

      <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <p className={`max-w-md text-xs ${isDark ? "text-white/50" : "text-ink/50"} leading-relaxed`}>
          By joining, you agree to receive launch updates and limited-drop alerts from GoneSoon.
        </p>
        <button
          data-testid="waitlist-submit"
          type="submit"
          disabled={submitting}
          className={isDark ? "btn-primary btn-on-dark" : "btn-primary"}
        >
          {submitting ? "Requesting…" : "Request early access"}
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </form>
  );
};

export default WaitlistForm;
